import { useEffect, useState } from "react";
import { MapPin, X, Check, Package, Timer, Store, Route } from "lucide-react";
import api from "@/api/client";
import { toast } from "sonner";

function money(n) { return `${Number(n || 0).toFixed(2)} د.ل`; }

/**
 * Full-screen offer modal shown to a driver when the backend auto-dispatch
 * routes a new order to them. They get 20 seconds (server-authoritative)
 * to Accept / Decline. On accept → same POST /driver/orders/:id/accept flow.
 * On decline → POST /driver/orders/:id/decline so the dispatcher fast-forwards
 * to the next closest driver.
 *
 * Rendered globally by DriverOffersListener via useDriverOffers().
 */
export default function OrderOfferModal({ offer, onAccepted, onDismiss }) {
  const [secondsLeft, setSecondsLeft] = useState(offer.expires_in_sec || 20);
  const [busy, setBusy] = useState(false);

  // Countdown driven by expires_at (server-authoritative). Auto-dismiss at 0.
  useEffect(() => {
    if (!offer.expires_at) return;
    const target = new Date(offer.expires_at).getTime();
    const tick = () => {
      const left = Math.max(0, Math.floor((target - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left === 0) onDismiss();
    };
    tick();
    const t = setInterval(tick, 500);
    return () => clearInterval(t);
  }, [offer.expires_at, onDismiss]);

  const accept = async () => {
    setBusy(true);
    try {
      await api.post(`/driver/orders/${offer.order_id}/accept`);
      toast.success("تم قبول الطلب");
      onAccepted(offer.order_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر قبول الطلب");
      onDismiss();
    } finally { setBusy(false); }
  };

  const decline = async () => {
    setBusy(true);
    try {
      await api.post(`/driver/orders/${offer.order_id}/decline`);
    } catch { /* silent — dispatcher moves on either way */ }
    finally { setBusy(false); onDismiss(); }
  };

  const rest = offer.restaurant || {};
  const addr = offer.customer_address || {};

  return (
    <div
      className="fixed inset-0 z-[190] bg-gradient-to-br from-[#1a2b6d] to-[#2547D0] flex flex-col text-white"
      data-testid="order-offer-modal"
      dir="rtl"
    >
      {/* Countdown ring */}
      <div className="flex-1 flex flex-col items-center justify-start px-6 pt-8 overflow-y-auto">
        <div className="w-28 h-28 rounded-full border-8 border-white/25 flex items-center justify-center mb-4 relative">
          <div
            className="absolute inset-[-8px] rounded-full border-8 border-transparent"
            style={{
              borderTopColor: "#fbbf24",
              transform: `rotate(${(1 - secondsLeft / 20) * 360}deg)`,
              transition: "transform 0.5s linear",
            }}
          />
          <div className="text-4xl font-black tabular-nums" dir="ltr" data-testid="offer-countdown">
            {secondsLeft}
          </div>
        </div>
        <div className="text-xs uppercase tracking-wide text-white/70 mb-1 flex items-center gap-1">
          <Timer size={12}/> طلب جديد لك — اقرب سائق للمطعم
        </div>
        <div className="text-2xl font-black mb-4" style={{ fontFamily: "Cairo" }} data-testid="offer-code">
          طلب #{offer.order_code || offer.order_id?.slice(0, 6)}
        </div>

        <div className="w-full max-w-md bg-white/10 backdrop-blur-sm rounded-2xl p-4 mb-3 border border-white/20">
          <div className="flex items-center gap-2 mb-2">
            <Store size={16} className="text-amber-300"/>
            <span className="font-black">{rest.name || "المطعم"}</span>
          </div>
          <div className="text-xs text-white/80 pr-6">{rest.address || ""}{rest.city ? `، ${rest.city}` : ""}</div>
        </div>

        <div className="w-full max-w-md bg-white/10 backdrop-blur-sm rounded-2xl p-4 mb-3 border border-white/20">
          <div className="flex items-center gap-2 mb-2">
            <MapPin size={16} className="text-red-300"/>
            <span className="font-black">عنوان الزبون</span>
          </div>
          <div className="text-xs text-white/80 pr-6">
            {addr.street ? `${addr.street}، ` : ""}{addr.city || ""}
          </div>
          {addr.notes && <div className="text-xs italic text-white/70 pr-6 mt-1">&ldquo;{addr.notes}&rdquo;</div>}
        </div>

        <div className="w-full max-w-md grid grid-cols-3 gap-2 mb-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-3 text-center border border-white/20">
            <Route size={16} className="mx-auto text-cyan-300 mb-1"/>
            <div className="text-[10px] text-white/70">من المطعم</div>
            <div className="font-black text-sm" data-testid="offer-distance">
              {offer.distance_from_you_km ? `${offer.distance_from_you_km} كم` : "-"}
            </div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-3 text-center border border-white/20">
            <Package size={16} className="mx-auto text-emerald-300 mb-1"/>
            <div className="text-[10px] text-white/70">التوصيل</div>
            <div className="font-black text-sm" data-testid="offer-fee">{money(offer.delivery_fee)}</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-3 text-center border border-white/20">
            <div className="text-[10px] text-white/70 mt-1">إجمالي الطلب</div>
            <div className="font-black text-sm" data-testid="offer-total">{money(offer.total)}</div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="pb-8 pt-4 px-6 grid grid-cols-2 gap-3 border-t border-white/15">
        <button
          onClick={decline}
          disabled={busy}
          className="py-4 rounded-2xl bg-red-500/90 hover:bg-red-500 flex items-center justify-center gap-2 font-black text-lg disabled:opacity-50"
          data-testid="offer-decline-btn"
        >
          <X size={20}/> رفض
        </button>
        <button
          onClick={accept}
          disabled={busy}
          className="py-4 rounded-2xl bg-emerald-500 hover:bg-emerald-600 flex items-center justify-center gap-2 font-black text-lg disabled:opacity-50 animate-pulse-dot"
          data-testid="offer-accept-btn"
        >
          <Check size={20}/> قبول
        </button>
      </div>
    </div>
  );
}
